"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import Link from "next/link";
import { ArrowLeft, X } from "lucide-react";
import {
  ALL_ACCEPTED,
  ALL_REJECTED,
  type CookieCategories,
  getConsent,
  OPEN_COOKIE_SETTINGS_EVENT,
  setConsent,
  subscribeConsent,
} from "@/lib/cookie-consent";

const btnBase =
  "inline-flex min-h-[44px] items-center justify-center rounded-[10px] px-5 text-[14px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60";
const btnPrimary = `${btnBase} bg-accent text-accent-foreground hover:opacity-90`;
const btnOutline = `${btnBase} border border-border text-foreground hover:border-accent hover:text-accent`;
const btnGhost = `${btnBase} text-muted-foreground underline-offset-4 hover:text-foreground hover:underline`;

// useSyncExternalStore snapshots. The server — and the first client render,
// for hydration — reports "do not prompt", so the banner is never in the SSR
// HTML and a returning visitor with a stored choice sees no flash. After
// hydration the real localStorage value is read; a new visitor gets the
// banner. This is the supported pattern for a browser-only data source — no
// reading localStorage inside an effect.
const getPromptSnapshot = () => getConsent() === null;
const getServerPromptSnapshot = () => false;

export function CookieConsent() {
  // True when the visitor has not yet made a cookie decision.
  const shouldPrompt = useSyncExternalStore(
    subscribeConsent,
    getPromptSnapshot,
    getServerPromptSnapshot,
  );

  // Whether the preferences panel is explicitly open (via "Manage
  // preferences" or a footer "Cookie settings" link).
  const [panelOpen, setPanelOpen] = useState(false);
  const [draft, setDraft] = useState<CookieCategories>(ALL_REJECTED);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // The panel wins; otherwise prompt until a decision exists; else nothing.
  const view: "hidden" | "banner" | "preferences" = panelOpen
    ? "preferences"
    : shouldPrompt
      ? "banner"
      : "hidden";

  // No decision yet → the panel offers "Back" to the banner. A returning
  // visitor → it offers "Close" (keep the existing choice untouched).
  const origin: "first-visit" | "settings" = shouldPrompt
    ? "first-visit"
    : "settings";

  const closePanel = useCallback(() => setPanelOpen(false), []);

  const openPreferences = useCallback(() => {
    const existing = getConsent();
    setDraft(existing ? existing.categories : ALL_REJECTED);
    setPanelOpen(true);
  }, []);

  const decide = useCallback((categories: CookieCategories) => {
    setConsent(categories); // notifies the store → `shouldPrompt` updates
    setPanelOpen(false);
  }, []);

  // A footer "Cookie settings" control reopens the panel from anywhere.
  useEffect(() => {
    function onOpen() {
      const existing = getConsent();
      setDraft(existing ? existing.categories : ALL_REJECTED);
      setPanelOpen(true);
    }
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, onOpen);
    return () =>
      window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, onOpen);
  }, []);

  // Land keyboard + screen-reader focus on the panel when it opens.
  useEffect(() => {
    if (view === "preferences") headingRef.current?.focus();
  }, [view]);

  // Escape closes the panel.
  useEffect(() => {
    if (view !== "preferences") return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setPanelOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [view]);

  if (view === "hidden") return null;

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      className="cookie-consent-bar fixed inset-x-0 bottom-0 z-50 border-t border-border bg-card shadow-[0_-2px_30px_rgba(0,0,0,0.28)]"
    >
      <div className="mx-auto w-full max-w-[1180px] px-6 py-5 sm:px-10 sm:py-6">
        {view === "banner" ? (
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between lg:gap-10">
            <div className="lg:max-w-[640px]">
              <h2 className="font-sans text-[19px] font-normal leading-snug text-foreground">
                Cookies on Thexis
              </h2>
              <p className="mt-1.5 text-[13.5px] leading-relaxed text-muted-foreground">
                We use cookies that are strictly necessary to run Thexis and
                keep you signed in. With your consent, we would also use cookies
                to remember your preferences and to understand how the site is
                used.{" "}
                <Link
                  href="/cookies"
                  className="font-medium text-foreground underline underline-offset-2 hover:text-accent"
                >
                  Read our Cookie Policy
                </Link>
                .
              </p>
            </div>
            <div className="flex flex-col gap-2.5 sm:flex-row lg:shrink-0">
              <button
                type="button"
                onClick={() => decide(ALL_REJECTED)}
                className={btnOutline}
              >
                Reject all
              </button>
              <button
                type="button"
                onClick={openPreferences}
                className={btnGhost}
              >
                Manage preferences
              </button>
              <button
                type="button"
                onClick={() => decide(ALL_ACCEPTED)}
                className={btnPrimary}
              >
                Accept all
              </button>
            </div>
          </div>
        ) : (
          <div className="max-h-[80vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-6">
              <div>
                <h2
                  ref={headingRef}
                  tabIndex={-1}
                  className="font-sans text-[21px] font-normal leading-snug text-foreground focus-visible:outline-none"
                >
                  Cookie preferences
                </h2>
                <p className="mt-1.5 max-w-[620px] text-[13.5px] leading-relaxed text-muted-foreground">
                  Choose which cookies Thexis can use. Strictly necessary
                  cookies are always on because the site cannot function
                  without them.
                </p>
              </div>
              <button
                type="button"
                onClick={closePanel}
                className="-mr-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
                aria-label={
                  origin === "settings"
                    ? "Close cookie preferences"
                    : "Back to cookie banner"
                }
              >
                {origin === "settings" ? (
                  <X size={18} />
                ) : (
                  <ArrowLeft size={18} />
                )}
              </button>
            </div>

            <div className="mt-5 border-t border-border">
              <CategoryRow
                title="Strictly necessary"
                description="Required for core features such as signing in, account security, and remembering your cookie choice. These cannot be switched off."
                control={
                  <span className="inline-flex h-7 items-center rounded-full border border-border px-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    Always on
                  </span>
                }
              />
              <CategoryRow
                title="Functional"
                description="Remembers preferences such as your light or dark theme and unsaved form drafts, so you do not have to set them again."
                control={
                  <Switch
                    label="Functional cookies"
                    checked={draft.functional}
                    onChange={(v) =>
                      setDraft((d) => ({ ...d, functional: v }))
                    }
                  />
                }
              />
              <CategoryRow
                title="Analytics"
                description="Helps us understand how the site is used so we can improve it. No analytics cookies are in use today; this preference will apply if we introduce them."
                control={
                  <Switch
                    label="Analytics cookies"
                    checked={draft.analytics}
                    onChange={(v) =>
                      setDraft((d) => ({ ...d, analytics: v }))
                    }
                  />
                }
                last
              />
            </div>

            <div className="mt-5 flex flex-col gap-2.5 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => decide(ALL_REJECTED)}
                className={btnOutline}
              >
                Reject all
              </button>
              <button
                type="button"
                onClick={() => decide(ALL_ACCEPTED)}
                className={btnOutline}
              >
                Accept all
              </button>
              <button
                type="button"
                onClick={() => decide(draft)}
                className={btnPrimary}
              >
                Save preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryRow({
  title,
  description,
  control,
  last = false,
}: {
  title: string;
  description: string;
  control: React.ReactNode;
  last?: boolean;
}) {
  return (
    <div
      className={[
        "flex items-start justify-between gap-5 py-4",
        last ? "" : "border-b border-border",
      ].join(" ")}
    >
      <div className="min-w-0">
        <h3 className="text-[14px] font-semibold text-foreground">{title}</h3>
        <p className="mt-1 max-w-[560px] text-[13px] leading-relaxed text-muted-foreground">
          {description}
        </p>
      </div>
      <div className="shrink-0">{control}</div>
    </div>
  );
}

function Switch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className="flex h-11 w-11 items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
    >
      <span
        className={[
          "relative h-6 w-11 rounded-full transition-colors",
          checked ? "bg-accent" : "bg-muted-foreground/35",
        ].join(" ")}
      >
        <span
          className={[
            "absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform motion-reduce:transition-none",
            checked ? "translate-x-[1.375rem]" : "translate-x-0",
          ].join(" ")}
        />
      </span>
    </button>
  );
}
